import { ColumnConfig, ColumnExtractor, ColumnText, DefaultFormatter, ISODateFormatter, LodashExtractor, NumberFormatter, NumberWithUnitsFormatter, SimpleExtractor } from '@ddt-web/data-service';
import { BfaConstants } from '@ddt-web/domain';

import { FundStatsUtil } from '../util';
import { ExposureOutliersDataExtractorUtil } from '../util/exposure-outliers-data-extractor.util';

export const SUPPLEMENTAL_PERFORMANCE_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_1Y_TOTAL_RETURN', '1Y Total Return'),
		new ColumnText('FUND_ANALYSIS_1Y_TOTAL_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnY1.numberValue'),
		'returnY1',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_TOTAL_RETURN', '3Y Total Return'),
		new ColumnText('FUND_ANALYSIS_3Y_TOTAL_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnY3.numberValue'),
		'returnY3',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_5Y_TOTAL_RETURN', '5Y Total Return'),
		new ColumnText('FUND_ANALYSIS_5Y_TOTAL_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnY5.numberValue'),
		'returnY5',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_10Y_TOTAL_RETURN', '10Y Total Return'),
		new ColumnText('FUND_ANALYSIS_10Y_TOTAL_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnY10.numberValue'),
		'returnY10',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_1Y_TOTAL_RETURN_RANK', '1Y Total Return Rank'),
		null,
		new LodashExtractor('funds.returnY1CategoryRank.numberValue'),
		'returnY1CategoryRank',
		new NumberFormatter(NumberFormatter.NONE),
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_TOTAL_RETURN_RANK', '3Y Total Return Rank'),
		null,
		new LodashExtractor('funds.returnY3CategoryRank.numberValue'),
		'returnY3CategoryRank',
		new NumberFormatter(NumberFormatter.NONE),
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_5Y_TOTAL_RETURN_RANK', '5Y Total Return Rank'),
		null,
		new LodashExtractor('funds.returnY5CategoryRank.numberValue'),
		'returnY5CategoryRank',
		new NumberFormatter(NumberFormatter.NONE),
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_10Y_TOTAL_RETURN_RANK', '10Y Total Return Rank'),
		null,
		new LodashExtractor('funds.returnY10CategoryRank.numberValue'),
		'returnY10CategoryRank',
		new NumberFormatter(NumberFormatter.NONE),
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_1Y_SHARPE_RATIO', '1Y Sharpe Ratio'),
		new ColumnText('FUND_ANALYSIS_1Y_SHARPE_RATIO_TOOLTIP'),
		new LodashExtractor('funds.rnrSharpeRatioY1.numberValue'),
		'1ySharpeRatio',
		new NumberFormatter(NumberFormatter.NONE)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_SHARPE_RATIO', '3Y Sharpe Ratio'),
		new ColumnText('FUND_ANALYSIS_3Y_SHARPE_RATIO_TOOLTIP'),
		new LodashExtractor('funds.rnrSharpeRatioY3.numberValue'),
		'3ySharpeRatio',
		new NumberFormatter(NumberFormatter.NONE)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_5Y_SHARPE_RATIO', '5Y Sharpe Ratio'),
		new ColumnText('FUND_ANALYSIS_5Y_SHARPE_RATIO_TOOLTIP'),
		new LodashExtractor('funds.rnrSharpeRatioY5.numberValue'),
		'5ySharpeRatio',
		new NumberFormatter(NumberFormatter.NONE)
	)
];

export const LONG_TERM_UNDER_PERFORMERS_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_PERCENT_TIME_4_QTILE', '% of time in the 4-qtile'),
		new ColumnText('FUND_ANALYSIS_PERCENT_TIME_4_QTILE_TOOLTIP'),
		new LodashExtractor('funds.36moPercRollingBottomQuartile.numberValue'),
		'percentTimeIn4Qtile',
		new NumberFormatter(NumberFormatter.PERCENT, BfaConstants.DEFAULT_DECIMAL_PLACES, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, 100)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_PERCENT_TIME_1_QTILE', '% of time in the 1-qtile'),
		new ColumnText('FUND_ANALYSIS_PERCENT_TIME_1_QTILE_TOOLTIP'),
		new LodashExtractor('funds.36moPercRollingTopQuartile.numberValue'),
		'percentTimeIn1Qtile',
		new NumberFormatter(NumberFormatter.PERCENT, BfaConstants.DEFAULT_DECIMAL_PLACES, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, 100)
	),
	...SUPPLEMENTAL_PERFORMANCE_COLUMNS
];

export const TRENDING_PERFORMANCE_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3M_RETURN', '3 Month Performance'),
		new ColumnText('FUND_ANALYSIS_3M_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnM3.numberValue'),
		'3MonthReturn',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_9M_LAGGED_PERFORMANCE', '9 Month Lagged Performance'),
		new ColumnText('FUND_ANALYSIS_9M_LAGGED_PERFORMANCE_TOOLTIP'),
		new LodashExtractor('funds.returnMo9.numberValue'),
		'm9LaggedPerformance',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	...SUPPLEMENTAL_PERFORMANCE_COLUMNS
];

export const EXPOSURE_OUTLIERS_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_LARGE', 'Exposure Outliers Large'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersLarge')),
		'exposureOutliersLarge',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_SMALL', 'Exposure Outliers Small'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersSmall')),
		'exposureOutliersSmall',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_FOREIGN_EQUITY', 'Exposure Outliers Foreign Equity'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersForeignEquity')),
		'exposureOutliersForeignEquity',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_FOREIGN_FIXED', 'Exposure Outliers Foreign Fixed'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersForeignFixed')),
		'exposureOutliersForeignFixed',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_FOREIGN_EMERGING', 'Exposure Outliers Emerging'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersEmerging')),
		'exposureOutliersEmerging',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPOSURE_OUTLIERS_FOREIGN_MISC', 'Exposure Outliers Misc'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'exposureOutliersMisc')),
		'exposureOutliersMisc',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(new ColumnText('FUND_ANALYSIS_EXPOSURE_TYPE', 'Exposure Type'),
		new ColumnText('FUND_ANALYSIS_EXPOSURE_TYPE_TOOLTIP'),
		new SimpleExtractor(['positionDetails'], ({ customClientProperties }) => {
			const relevantSubCta: string = ExposureOutliersDataExtractorUtil.getRelevantExposureOutliersSubAofs()
				.find(id => customClientProperties[id] === 'true');
			if (!!relevantSubCta) {
				return `EXPOSURE_TYPE_${relevantSubCta}`;
			}
			return '-';
		}),
		'exposureType',
		DefaultFormatter.instance
	),
	new ColumnConfig(new ColumnText('FUND_ANALYSIS_PERCENT_EXPOSURE_ALLOC', '% Allocation to exposure'),
		new ColumnText('FUND_ANALYSIS_PERCENT_EXPOSURE_ALLOC_TOOLTIP'),
		new SimpleExtractor(['positionDetails'], (position) => ExposureOutliersDataExtractorUtil.calculateAllocationToExposure(position)),
		'percentExposureAlloc',
		new NumberFormatter(NumberFormatter.PERCENT)
	)
];

export const DUBIOUS_DIVERSIFIERS_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_SNP_CORRELATION', '3yr Correlation to S&P (ex-ante)'),
		new ColumnText('FUND_ANALYSIS_3Y_SNP_CORRELATION_TOOLTIP'),
		new LodashExtractor('funds.correlationY3.numberValue'),
		'3ySnpCorrelation',
		new NumberFormatter(NumberFormatter.NONE)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_BETA', '3yr Beta to S&P500'),
		new ColumnText('FUND_ANALYSIS_3Y_BETA_TOOLTIP'),
		new LodashExtractor('funds.betaSP3Y.numberValue'),
		'3ySnpBeta',
		new NumberFormatter(NumberFormatter.NONE)
	)
];

export const WORST_VALUE_FOR_MONEY_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_NET_EXPENSE_RATIO', 'Net Expense Ratio'),
		new ColumnText('FUND_ANALYSIS_NET_EXPENSE_RATIO_TOOLTIP'),
		new LodashExtractor('assetAnalytics.expenseRatio'),
		'netExpenseRatio',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_ALPHA', '3Y Alpha'),
		new ColumnText('FUND_ANALYSIS_3Y_ALPHA_TOOLTIP'),
		new LodashExtractor('funds.rnrProspectusBenchmarkAlphaY3.numberValue'),
		'3yAlpha',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_PROSPECTUS_BENCHMARK_WORST_VALUE', 'Prospectus Benchmark'),
		new ColumnText('FUND_ANALYSIS_PROSPECTUS_BENCHMARK_WORST_VALUE_TOOLTIP'),
		new LodashExtractor('customClientProperties.prospectusPrimaryBenchmarkName'),
		'prospectusBenchmarkWorstValue'
	)
];

export const HIGH_TAX_OFFENDERS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_OFFENDERS_R1', 'Tax Offenders Rule 1'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'taxOffendersR1')),
		'taxOffendersR1',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_OFFENDERS_R2', 'Tax Offenders Rule 2'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'taxOffendersR2')),
		'taxOffendersR2',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_OFFENDERS_R3', 'Tax Offenders Rule 3'),
		null,
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'taxOffendersR3')),
		'taxOffendersR3',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_1Y_TAX_COST', '1Y Tax Cost'),
		new ColumnText('FUND_ANALYSIS_1Y_TAX_COST_TOOLTIP'),
		new LodashExtractor('funds.taxcostRatioyr1.numberValue'),
		'1yTaxCost',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_TAX_COST', '3Y Tax Cost'),
		new ColumnText('FUND_ANALYSIS_3Y_TAX_COST_TOOLTIP'),
		new LodashExtractor('funds.taxcostRatioyr3.numberValue'),
		'3yTaxCost',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_1Y_TOTAL_RETURN', '1Y Total Return'),
		new ColumnText('FUND_ANALYSIS_TAX_1Y_TOTAL_RETURN_TOOLTIP'),
		new LodashExtractor('funds.returnY1.numberValue'),
		'taxReturnY1',
		new NumberFormatter(NumberFormatter.PERCENT)
	)
];

export const WORST_TAX_OFFENDERS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_WORST_TAX_OFFENDERS', 'Worst Tax Offenders'),
		new ColumnText('FUND_ANALYSIS_WORST_TAX_OFFENDERS_TOOLTIP'),
		new SimpleExtractor([], (position) => FundStatsUtil.checkSubAofColour(position.taxDetailsFund, 'taxWorstOffenderFlag')),
		'taxWorstOffenderFlag',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_ESTIMATES_TOTAL_PCT', 'Pending Capital Gain %'),
		new ColumnText('FUND_ANALYSIS_TAX_ESTIMATES_TOTAL_PCT_TOOLTIP'),
		new SimpleExtractor([], position => position.taxDetailsFund?.assetAnalytics.taxEstimatesTotalPct),
		'taxEstimatesTotalPct',
		NumberFormatter.create({ multiplier: 100, suffix: NumberFormatter.PERCENT })
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_TAX_ESTIMATES_SELL_DATE', 'Capital Gain Distribution Date'),
		new ColumnText('FUND_ANALYSIS_TAX_ESTIMATES_SELL_DATE_TOOLTIP'),
		new SimpleExtractor([], position => position.taxDetailsFund?.customClientProperties.taxEstimatesSellDate),
		'taxEstimatesSellDate',
		new ISODateFormatter(ISODateFormatter.SHORT)
	),
	...HIGH_TAX_OFFENDERS
];

export const MONEY_IN_MOTION_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_6M_FLOWS_NAV', '6 Month Fund Flows (%NAV)'),
		new ColumnText('FUND_ANALYSIS_6M_FLOWS_NAV_TOOLTIP'),
		new LodashExtractor('funds.netFlows.numberValue'),
		'6mFlowsNav',
		new NumberFormatter(NumberFormatter.PERCENT, BfaConstants.DEFAULT_DECIMAL_PLACES, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, 100)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_FUND_NAV', 'Fund NAV'),
		new ColumnText('FUND_ANALYSIS_FUND_NAV_TOOLTIP'),
		new LodashExtractor('customClientProperties.fundNetAsset'),
		'fundNetAsset',
		new NumberWithUnitsFormatter(null, '-')
	)
];
export const DOWNSIDE_DANGERS_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_DOWNSIDE_DANGERS_CAPTURE', 'Downside Dangers Capture'),
		new ColumnText('FUND_ANALYSIS_DOWNSIDE_DANGERS_CAPTURE_TOOLTIP'),
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'downsideDangersCapture')),
		'downsideDangersCapture',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_DOWNSIDE_DANGERS_DRAWDOWN', 'Downside Dangers Drawdown'),
		new ColumnText('FUND_ANALYSIS_DOWNSIDE_DANGERS_CAPTURE_TOOLTIP'),
		new SimpleExtractor(['positionDetails'], position => FundStatsUtil.checkSubAofColour(position, 'downsideDangersDrawdown')),
		'downsideDangersDrawdown',
		DefaultFormatter.instance,
		null,
		false
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_UPSIDE_CAPTURE', '3Y Upside Capture'),
		new ColumnText('FUND_ANALYSIS_3Y_UPSIDE_CAPTURE_TOOLTIP'),
		new LodashExtractor('funds.rnrCaptureRatioUpY3.numberValue'),
		'3yUpsideCapture',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_DOWNSIDE_CAPTURE', '3Y Downside Capture'),
		new ColumnText('FUND_ANALYSIS_3Y_DOWNSIDE_CAPTURE_TOOLTIP'),
		new LodashExtractor('funds.rnrCaptureRatioDownY3.numberValue'),
		'3yDownsideCapture',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_MAX_DRAWDOWN', '3Y Max Drawdown'),
		new ColumnText('FUND_ANALYSIS_3Y_MAX_DRAWDOWN_TOOLTIP'),
		new LodashExtractor('customClientProperties.rnrMaximumDrawdownY3'),
		'3yMaxDrawdown',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_PROSPECTUS_BENCHMARK_DOWNSIDE_DANGERS', 'Prospectus Benchmark'),
		new ColumnText('FUND_ANALYSIS_PROSPECTUS_BENCHMARK_DOWNSIDE_DANGERS_TOOLTIP'),
		new LodashExtractor('customClientProperties.morningstarPrimaryBenchmarkName'),
		'prospectusBenchmarkDownsideDangers'
	)
];
export const HIDDEN_RISK_COLUMNS: ColumnConfig<ColumnExtractor>[] = [
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_EXPECTED_SHORTFALL', 'Expected Shortfall'),
		new ColumnText('FUND_ANALYSIS_EXPECTED_SHORTFALL_TOOLTIP'),
		new LodashExtractor('funds.expectedShortfall.numberValue'),
		'expectedShortfall',
		new NumberFormatter(NumberFormatter.PERCENT, BfaConstants.DEFAULT_DECIMAL_PLACES, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, 100)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_ALADDIN_RISK', 'Aladdin Risk'),
		new ColumnText('FUND_ANALYSIS_ALADDIN_RISK_TOOLTIP'),
		new LodashExtractor('funds.aladdinRisk.numberValue'),
		'aladdinRisk',
		new NumberFormatter(NumberFormatter.PERCENT, BfaConstants.DEFAULT_DECIMAL_PLACES, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, 100)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_1Y_HISTORICAL_RISK', '1Y Historical Risk'),
		new ColumnText('FUND_ANALYSIS_1Y_HISTORICAL_RISK_TOOLTIP'),
		new LodashExtractor('funds.rnrStandardDeviationY1.numberValue'),
		'1yHistoricalRisk',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_3Y_HISTORICAL_RISK', '3Y Historical Risk'),
		new ColumnText('FUND_ANALYSIS_3Y_HISTORICAL_RISK_TOOLTIP'),
		new LodashExtractor('funds.rnrStandardDeviationY3.numberValue'),
		'3yHistoricalRisk',
		new NumberFormatter(NumberFormatter.PERCENT)
	),
	new ColumnConfig(
		new ColumnText('FUND_ANALYSIS_5Y_HISTORICAL_RISK', '5Y Historical Risk'),
		new ColumnText('FUND_ANALYSIS_5Y_HISTORICAL_RISK_TOOLTIP'),
		new LodashExtractor('funds.rnrStandardDeviationY5.numberValue'),
		'5yHistoricalRisk',
		new NumberFormatter(NumberFormatter.PERCENT)
	)
];

export const DATA_COLUMNS_PER_AOF: { [aofId: string]: ColumnConfig<ColumnExtractor>[] } = {
	'longTermUnderperformers': LONG_TERM_UNDER_PERFORMERS_COLUMNS,
	'trendingPerformance': TRENDING_PERFORMANCE_COLUMNS,
	'exposureOutliers': EXPOSURE_OUTLIERS_COLUMNS,
	'dubiousDiversifiers': DUBIOUS_DIVERSIFIERS_COLUMNS,
	'worstValueForMoney': WORST_VALUE_FOR_MONEY_COLUMNS,
	'highTaxOffenders': HIGH_TAX_OFFENDERS,
	'worstTaxOffenders': WORST_TAX_OFFENDERS,
	'moneyInMotion': MONEY_IN_MOTION_COLUMNS,
	'downsideDangers': DOWNSIDE_DANGERS_COLUMNS,
	'hiddenRisk': HIDDEN_RISK_COLUMNS
};
